import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { getLoremIpsum } from '../../../functions/lorem-ispum';
import { Action } from '../../../models/core/action';
import { Format } from '../../../pipes/format.pipe';
import { ExportService } from '../../../services/export/export.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ClaimLabelComponent } from '../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../core/labels/tag.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { getExpandSubject } from '../../../functions/get-expand-subject';

@Component({
  selector: 'app-diary-table-top',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    ActionRowComponent,
    ButtonComponent,
    CommonModule,
    ActionButtonComponent,
    MenuComponent,
    TagComponent,
    ReactiveFormsModule,
    ClaimLabelComponent,
  ],
  template: `
    @if (isPageTodo) {
      <app-action-row useMobileLayout>
        <app-button left1 (click)="newDiary()" label="New diary" />
        <app-button
          left3
          emphasis="Low"
          (click)="editTypes()"
          label="Diary types"
        />
      </app-action-row>
    } @else {
      <app-action-row useMobileLayout>
        <app-button left1 (click)="newClaimDiary()" label="New diary" />
        <app-button
          left3
          emphasis="Low"
          (click)="editTypes()"
          label="Diary types"
        />
      </app-action-row>
    }
    <app-table
      [dataSource]="diaryEntries"
      [loading]="loading"
      [error]="error"
      [loadingHeight]="280"
      loadingStyle="Box"
      [pageSize]="10"
      [showSelection]="showSelection"
      [showExport]="true"
      [expandable]="true"
      [hideExpandAll]="false"
      dataKey="id"
      [scrollable]="false"
      (configure)="showPickList = true"
      (selectedEvent)="onSelection($event)"
      recordLabel="diary"
      recordLabelPlural="diaries"
      [ariaTableLabel]="'Diaries'"
      [rowExpandLabel]="getDiaryRowExpandLabel"
      (export)="onExport($event)"
      [globalFilterFields]="[
        'status',
        'case',
        'fileNumber',
        'formattedDueDate',
        'formattedNotificationDate',
        'firstName',
        'lastName',
        'message',
        'diaryType',
        'formattedUsers',
      ]"
    >
      <app-column name="status" label="Status">
        <ng-template let-data>
          <app-tag [type]="getTagStyle(data.status)">{{ data.status }}</app-tag>
        </ng-template>
      </app-column>
      <app-column name="case" label="Claim">
        <ng-template let-data>
          @if (data.recordSource) {
            <app-claim-label
              [claim]="data"
              [showWatchButton]="!!data.fileNumber"
              [recordSource]="data.recordSource"
              [recordId]="data.navigatingRecID ?? data.recordId"
              [fileNumFormat]="data?.format"
            ></app-claim-label>
          } @else {
            <span>{{ data.fileNumber || '-' }}</span>
          }
        </ng-template>
      </app-column>
      <app-column name="fullName" label="Name" />
      <app-column name="dueDate" label="Due Date" format="d" />
      <app-column
        name="notificationDate"
        label="Notification Time"
        format="t"
      />
      <app-column name="formattedUsers" label="Follow up user">
        <ng-template let-data>
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-max-w-full tw-whitespace-normal">
              {{ data.formattedUsers }}
            </div>
          </div>
        </ng-template>
      </app-column>
      <app-column name="diaryType" label="Diary Type" />
      <app-column name="actions" label="Actions" [sort]="false">
        <ng-template let-data>
          <app-action-button
            [menus]="[completeMenu, editMenu]"
            [row]="data"
            (click)="toggleActionMenu($event, data)"
          />
        </ng-template>
      </app-column>
      <ng-template #expand let-data>
        <div
          class="tw-whitespace-pre-line tw-py-2 tw-pr-2"
          role="region"
          [attr.aria-label]="'Message for diary ' + data.id"
        >
          <span class="tw-font-bold tw-pr-2">Message:</span>
          {{ data.message }}
        </div>
      </ng-template>
    </app-table>
    <app-menu
      [items]="completeActions"
      #completeMenu
      (click)="onMenu($event, completeMenu.data)"
    />
    <app-menu
      [items]="editActions"
      #editMenu
      (click)="onMenu($event, editMenu.data)"
    />
  `,
  styles: ``,
})
export class DiaryTableTopComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  /** Prefer claim number + diary type over Status ("Past due"). */
  protected getDiaryRowExpandLabel = (row: any) =>
    getExpandSubject(row?.fileNumber ?? row?.case, row?.diaryType) || 'Diary';

  [x: string]: any;
  @Output() searchCountEvent = new EventEmitter<number>();
  @Output() markCompleteEvent = new EventEmitter<number>();

  @Input()
  showDateAccessed = false;

  @Input()
  showSelection = true;

  @Input()
  diaryEntries?: any = null;

  @Input()
  isPageTodo = false;

  @Input()
  loading = false;

  @Input()
  error: any = null;

  @ViewChild('completeMenu') completeMenu!: MenuComponent;
  @ViewChild('editMenu') editMenu!: MenuComponent;

  claim: any = null;
  showPickList = false;
  loaded = false;
  disableMarkComplete = true;
  dataSource: any[] = [];

  completeActions: Action[] = [
    { label: 'Mark complete', key: 'complete' },
    { label: 'Edit', key: 'edit' },
  ];

  editActions: Action[] = [{ label: 'Edit', key: 'edit' }];

  async onExport(format: ExportFormat) {
    try {
      let columns = [
        { propertyName: 'status', headerText: 'Status' },
        { propertyName: 'fileNumber', headerText: 'Claim' },
        { propertyName: 'firstName', headerText: 'First Name' },
        { propertyName: 'lastName', headerText: 'Last Name' },
        { propertyName: 'dueDate', headerText: 'Due Date', format: 'MM/dd/yyyy' },
        { propertyName: 'message', headerText: 'Description' },
        { propertyName: 'diaryType', headerText: 'Diary Type' },
        { propertyName: 'formattedUsers', headerText: 'Follow up user' },
        { propertyName: 'notificationDate', headerText: 'Notification Time' },
      ];
      const excludedProperties = new Set();
      if (!(this as any).showTitle) excludedProperties.add('title');
      columns = columns.filter(
        (col) => !excludedProperties.has(col.propertyName),
      );
      const rows = this.diaryEntries;
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `Diary_${formattedDate}.${format}`;
      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Diary');
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'Diary',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (err) {
      if ((this as any).handleError) {
        (this as any).handleError(err);
      } else {
        (this as any).error = err;
      }
    }
  }

  async ngOnInit() {
    this.mockData();
    this.searchCountEvent.emit(this.dataSource.length);

    if (!this.isPageTodo) {
      this.claim = this.view.currentClaim();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.diaryEntries) {
      this.searchCountEvent.emit(this.diaryEntries?.length || 0);
    }
  }

  getBackData() {
    const data = {
      backRouterLink: document.location.pathname,
      backLabel: 'My to-do',
    };
    return data;
  }

  newDiary() {
    this.storage.set('current-diary', this.getBackData());
    this.router.navigate(['/diary-edit']);
  }

  newClaimDiary() {
    const data = {
      ...this.getBackData(),
      isClaimSpecific: true,
      claimUID: this.claim?.recordId,
      clientNumber: this.claim?.contractNumber,
      RecordSource: this.claim?.recordSource,
      fileNumber: this.claim?.fileNumber,
      ...(this.claim?.recordSource === 'T' && {
        employeeUID: this.claim?.employeeId,
      }),
    };
    this.storage.set('current-diary', data);
    this.router.navigate(['/diary-edit']);
  }

  editTypes() {
    const data = {
      ...this.getBackData(),
      ...(this.claim && {
        isClaimSpecific: true,
        clientNumber: this.claim.contractNumber,
      }),
    };
    this.storage.set('current-type', data);
    this.router.navigate(['/diary-types']);
  }

  onMenu(event: any, data: any) {
    if (event.key === 'edit') {
      data.backRouterLink = document.location.pathname;
      data.backLabel = 'My to-do';

      if (!this.isPageTodo && this.claim) {
        data.isClaimSpecific = true;
        data.claimUID = this.claim.recordId;
        data.clientNumber = this.claim.contractNumber;
        data.RecordSource = this.claim.recordSource;
        data.fileNumber = this.claim.fileNumber;
        if (this.claim.recordSource === 'T') {
          data.employeeUID = this.claim.employeeId;
        }
      }

      this.storage.set('current-diary', data);
      this.router.navigate(['/diary-edit']);
    } else if (event.key === 'complete') {
      this.markCompleteEvent.emit(data.id);
    }
  }

  mockData() {
    const max = document.location.pathname === '/todo' ? 10 : 2;
    for (let index = 0; index < max; index++) {
      this.dataSource.push({
        id: index,
        case: 119534,
        firstName: 'Lsomivn',
        lastName: 'Wxkbncm',
        dueDate: '2022-06-16T00:00:00',
        notificationDate: '2022-06-14T00:00:00',
        completed: '2022-06-20T00:00:00',
        followFirstName: 'Diary',
        followLastName: 'Diary',
        followID: 'TUSER03B',
        diaryType: 'MD - Medical Status/Management',
        status:
          index === 0
            ? 'Open'
            : index === 1
              ? 'Past due'
              : index === 2
                ? 'Completed'
                : index === 3
                  ? 'Draft'
                  : 'Open',
        update: 'Update',
        message: getLoremIpsum(),
        priority: 'Low',
      });
    }
  }

  getTagStyle(status: string): TagType {
    switch (status) {
      case 'Open':
        return 'Info';
      case 'Past due':
        return 'Error';
      case 'Draft':
        return 'Warn';
      case 'Completed':
        return 'Success';
      default:
        return 'Success';
    }
  }

  onSelection(selections: any[]) {
    this.disableMarkComplete = selections.length === 0;
  }

  getFormat(name: string): Format {
    switch (name) {
      case 'DateAccessed':
        return 'dt';
      case 'PtdTotal':
        return 'c';
      case 'DateClosed':
      case 'DateBegin':
      case 'DateEnd':
      case 'DateBirth':
      case 'DateOpen':
        return 'd';
      default:
        return null;
    }
  }

  toggleActionMenu(event: any, data: any) {
    if (
      data.allowOthers ||
      data.createdBy.includes(`(${this.session.user.userName})`)
    ) {
      this.completeMenu.toggle(event, data);
    } else {
      this.editMenu.toggle(event, data);
    }
  }
}
